/** @file CubeGround: the ground ring under the orientation cube, its +X / +Y labels and the facing marker. */
import type { ReactElement } from 'react';
import { compassAngle } from './compassAngle';
import { ringPoint } from './ringPoint';
import { VIEWPORT_COLORS } from './viewportColors';

/** Camera yaw / pitch, body facing (pose yaw, 0 = south), the cube's px per half-edge and centre, the ring radius. */
export type CubeGroundProps = {
  yawDeg: number; pitchDeg: number; facingDeg: number; scale: number; cx: number; cy: number; radius: number;
  /** `ring` is drawn before the cube (behind it), `marker` after it (always visible). */
  part: 'ring' | 'marker';
  /** The cube's screen box `[minX, minY, maxX, maxY]` in px: a far-side label inside it is hidden behind the cube. */
  box?: [number, number, number, number];
};

const RAD = Math.PI / 180;
/** The ground axes labelled on the ring, as the scene's axis lines run: +X east (red), +Y north (green). */
const GROUND_AXES = [
  { label: '+X', bearing: 90, color: VIEWPORT_COLORS.axisX },
  { label: '+Y', bearing: 0, color: VIEWPORT_COLORS.axisY },
] as const;

/** Whether `(x, y)` lies in `box` grown by a label's half size. */
const inside = (box: [number, number, number, number] | undefined, x: number, y: number): boolean =>
  box !== undefined && x > box[0] - 6 && x < box[2] + 6 && y > box[1] - 4 && y < box[3] + 4;

/**
 * The ring lies on the ground plane under the cube (its bottom, one half-edge down), drawn orthographically
 * (`ringPoint`). The ring labels the ground axis lines (+X east red, +Y north green) and is the compass handle
 * (a left drag on it orbits the camera, `useCompassDrag`); the marker is the body facing.
 */
export function CubeGround(props: CubeGroundProps): ReactElement {
  const { scale, cx } = props;
  const sp = Math.sin(props.pitchDeg * RAD);
  const cy = props.cy + Math.cos(props.pitchDeg * RAD) * scale;
  const r = props.radius * scale;
  const onRing = (bearingDeg: number, k = 1): [number, number] => ringPoint(props, bearingDeg, k);
  if (props.part === 'ring') {
    return (
      <g style={{ pointerEvents: 'none' }}>
        <ellipse data-testid="cube-compass" cx={cx} cy={cy} rx={r} ry={Math.max(0.5, Math.abs(r * sp))} fill="#141a17aa" stroke="#3b5e41"
          style={{ pointerEvents: 'visiblePainted', cursor: 'grab' }}>
          <title>Drag to orbit the camera</title>
        </ellipse>
        {GROUND_AXES.map((g) => {
          const [tx, ty] = onRing(g.bearing);
          const [lx, ly] = onRing(g.bearing, 1.22);
          const far = Math.cos(compassAngle(g.bearing, props.yawDeg) * RAD) > 0;
          return (
            <g key={g.label} data-ground-axis={g.label}>
              <circle cx={tx} cy={ty} r={1.8} fill={g.color} />
              {!(far && inside(props.box, lx, ly)) && <text x={lx} y={ly + 3} textAnchor="middle" fontSize={8} fontWeight={600} fill={g.color}>{g.label}</text>}
            </g>
          );
        })}
      </g>
    );
  }
  const [fx, fy] = onRing(180 + props.facingDeg);
  return (
    <circle data-testid="facing-marker" cx={fx} cy={fy} r={3} fill={VIEWPORT_COLORS.hover} stroke="#141a17" style={{ pointerEvents: 'none' }}>
      <title>body facing</title>
    </circle>
  );
}
